import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { 
  DashboardMetrics, 
  AgentStats, 
  DashboardAlert, 
  DashboardFilters,
  AlertType,
  AlertSeverity
} from '@/types/support.types';

// ID do departamento Customer Service
const CS_DEPARTMENT_ID = 'a1b2c3d4-e5f6-7890-abcd-ef1234567890';

interface UseManagerDashboardOptions {
  filters?: DashboardFilters;
}

export function useManagerDashboard(options: UseManagerDashboardOptions = {}) {
  const { filters = { period: 'today' } } = options;

  // Calculate date range based on period
  const getDateRange = () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filters.period) {
      case 'week':
        const startOfWeek = new Date(startOfToday);
        startOfWeek.setDate(startOfWeek.getDate() - 7);
        return { start: startOfWeek.toISOString(), end: now.toISOString() };
      case 'month':
        const startOfMonth = new Date(startOfToday);
        startOfMonth.setDate(startOfMonth.getDate() - 30);
        return { start: startOfMonth.toISOString(), end: now.toISOString() };
      default: // today
        return { start: startOfToday.toISOString(), end: now.toISOString() };
    }
  };

  // Query for aggregated metrics
  const metricsQuery = useQuery({
    queryKey: ['manager-dashboard-metrics', filters],
    queryFn: async (): Promise<DashboardMetrics> => {
      const dateRange = getDateRange();
      const today = new Date();
      const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
      const startOfWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const startOfMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();

      // Fetch all sessions from the view
      const { data: sessions, error } = await supabase
        .from('v_sessions_with_sla')
        .select('*');

      if (error) throw error;

      const allSessions = sessions || [];

      // Calculate totals by status
      const activeSessions = allSessions.filter(s => 
        s.status && ['new', 'in_progress', 'waiting_client', 'waiting_internal'].includes(s.status)
      );
      
      const total_active = activeSessions.length;
      const total_new = allSessions.filter(s => s.status === 'new').length;
      const total_in_progress = allSessions.filter(s => s.status === 'in_progress').length;
      const total_waiting = allSessions.filter(s => 
        s.status && ['waiting_client', 'waiting_internal'].includes(s.status)
      ).length;

      // Resolved counts by period
      const resolvedSessions = allSessions.filter(s => s.status === 'resolved' && s.resolved_at);
      const total_resolved_today = resolvedSessions.filter(s => 
        new Date(s.resolved_at!) >= new Date(startOfToday)
      ).length;
      const total_resolved_week = resolvedSessions.filter(s => 
        new Date(s.resolved_at!) >= new Date(startOfWeek)
      ).length;
      const total_resolved_month = resolvedSessions.filter(s => 
        new Date(s.resolved_at!) >= new Date(startOfMonth)
      ).length;

      // Unassigned count
      const unassigned_count = allSessions.filter(s => 
        !s.assigned_to && s.status !== 'resolved'
      ).length;

      // SLA compliance (for active + recently resolved)
      const sessionsWithSla = allSessions.filter(s => s.sla_status);
      const okOrWarning = sessionsWithSla.filter(s => 
        s.sla_status === 'ok' || s.sla_status === 'warning' || s.sla_status === 'responded'
      ).length;
      const sla_compliance_percent = sessionsWithSla.length > 0 
        ? Math.round((okOrWarning / sessionsWithSla.length) * 100)
        : 100;

      // Average first response time
      const sessionsWithResponse = allSessions.filter(s => 
        s.minutes_to_first_response !== null && s.minutes_to_first_response > 0
      );
      const avg_first_response_min = sessionsWithResponse.length > 0
        ? Math.round(
            sessionsWithResponse.reduce((acc, s) => acc + (s.minutes_to_first_response || 0), 0) / 
            sessionsWithResponse.length
          )
        : null;

      // Average resolution time
      const resolvedWithTime = resolvedSessions.filter(s => 
        s.minutes_open !== null && s.minutes_open > 0
      );
      const avg_resolution_min = resolvedWithTime.length > 0
        ? Math.round(
            resolvedWithTime.reduce((acc, s) => acc + (s.minutes_open || 0), 0) / 
            resolvedWithTime.length
          )
        : null;

      // Filter sessions for period-based metrics
      const periodStart = new Date(dateRange.start);
      const sessionsInPeriod = allSessions.filter(s => 
        s.created_at && new Date(s.created_at) >= periodStart
      );
      const resolvedInPeriod = resolvedSessions.filter(s =>
        s.resolved_at && new Date(s.resolved_at) >= periodStart
      );

      // Metrics by agent
      const agentMap = new Map<string, AgentStats>();
      
      // Count total CS sessions for distribution percentage
      let totalCsSessions = 0;
      
      for (const session of sessionsInPeriod) {
        if (!session.assigned_to) continue;
        if (session.agent_department_id !== CS_DEPARTMENT_ID) continue;
        
        totalCsSessions++;
        const agentId = session.assigned_to;
        const agentName = session.agent_name || 'Desconhecido';
        
        if (!agentMap.has(agentId)) {
          agentMap.set(agentId, {
            agent_id: agentId,
            agent_name: agentName,
            active_sessions: 0,
            resolved_today: 0,
            sla_compliance: 0,
            avg_response_time: null,
            total_sessions: 0,
            resolved_period: 0,
            sla_breached: 0,
            avg_resolution_min: null,
            distribution_percent: 0,
          });
        }
        
        const agent = agentMap.get(agentId)!;
        agent.total_sessions++;
        
        // Count active sessions
        if (session.status && ['new', 'in_progress', 'waiting_client', 'waiting_internal'].includes(session.status)) {
          agent.active_sessions++;
        }
        
        // Count resolved today
        if (session.status === 'resolved' && session.resolved_at && 
            new Date(session.resolved_at) >= new Date(startOfToday)) {
          agent.resolved_today++;
        }

        // Count resolved in selected period
        if (session.status === 'resolved' && session.resolved_at &&
            new Date(session.resolved_at) >= periodStart) {
          agent.resolved_period++;
        }

        // Count SLA breached
        if (session.sla_status === 'breached') {
          agent.sla_breached++;
        }
      }

      // Calculate per-agent SLA compliance, avg response time, avg resolution time, and distribution %
      for (const [agentId, agent] of agentMap) {
        // SLA compliance
        const agentSessions = sessionsInPeriod.filter(s => 
          s.assigned_to === agentId && s.sla_status
        );
        const agentOk = agentSessions.filter(s => 
          s.sla_status === 'ok' || s.sla_status === 'warning' || s.sla_status === 'responded'
        ).length;
        agent.sla_compliance = agentSessions.length > 0
          ? Math.round((agentOk / agentSessions.length) * 100)
          : 100;

        // Average response time per agent
        const agentWithResponse = sessionsInPeriod.filter(s => 
          s.assigned_to === agentId && 
          s.minutes_to_first_response !== null && 
          s.minutes_to_first_response > 0
        );
        agent.avg_response_time = agentWithResponse.length > 0
          ? Math.round(
              agentWithResponse.reduce((acc, s) => acc + (s.minutes_to_first_response || 0), 0) / 
              agentWithResponse.length
            )
          : null;

        // Average resolution time per agent
        const agentResolved = sessionsInPeriod.filter(s =>
          s.assigned_to === agentId &&
          s.status === 'resolved' &&
          s.minutes_open !== null &&
          s.minutes_open > 0
        );
        agent.avg_resolution_min = agentResolved.length > 0
          ? Math.round(
              agentResolved.reduce((acc, s) => acc + (s.minutes_open || 0), 0) /
              agentResolved.length
            )
          : null;

        // Distribution percentage
        agent.distribution_percent = totalCsSessions > 0
          ? Math.round((agent.total_sessions / totalCsSessions) * 100)
          : 0;
      }

      return {
        total_active,
        total_new,
        total_in_progress,
        total_waiting,
        total_resolved_today,
        total_resolved_week,
        total_resolved_month,
        sla_compliance_percent,
        avg_first_response_min,
        avg_resolution_min,
        unassigned_count,
        by_agent: Array.from(agentMap.values()).sort((a, b) => 
          b.total_sessions - a.total_sessions
        ),
      };
    },
    refetchInterval: 30000,
  });

  // Query for alerts
  const alertsQuery = useQuery({
    queryKey: ['manager-dashboard-alerts'],
    queryFn: async (): Promise<DashboardAlert[]> => {
      const alerts: DashboardAlert[] = [];
      const now = new Date();
      const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000).toISOString();
      const eightHoursAgo = new Date(now.getTime() - 8 * 60 * 60 * 1000).toISOString();

      const { data: breachedSessions } = await supabase
        .from('v_sessions_with_sla')
        .select('id')
        .eq('sla_status', 'breached')
        .neq('status', 'resolved');

      if (breachedSessions && breachedSessions.length > 0) {
        alerts.push({
          id: 'sla_breach',
          type: 'sla_breach' as AlertType,
          severity: 'critical' as AlertSeverity,
          title: 'SLA Violado',
          description: `${breachedSessions.length} sessão(ões) com SLA violado`,
          count: breachedSessions.length,
          session_ids: breachedSessions.map(s => s.id).filter(Boolean) as string[],
        });
      }

      const { data: unassignedSessions } = await supabase
        .from('support_sessions')
        .select('id')
        .is('assigned_to', null)
        .neq('status', 'resolved')
        .lt('created_at', fifteenMinutesAgo);

      if (unassignedSessions && unassignedSessions.length > 0) {
        alerts.push({
          id: 'unassigned',
          type: 'unassigned' as AlertType,
          severity: 'warning' as AlertSeverity,
          title: 'Sem Atendente',
          description: `${unassignedSessions.length} sessão(ões) sem atendente há 15+ min`,
          count: unassignedSessions.length,
          session_ids: unassignedSessions.map(s => s.id),
        });
      }

      const { data: pendingEscalations } = await supabase
        .from('session_escalations')
        .select('id, session_id')
        .eq('status', 'pending')
        .lt('escalated_at', eightHoursAgo);

      if (pendingEscalations && pendingEscalations.length > 0) {
        const uniqueSessionIds = [...new Set(pendingEscalations.map(e => e.session_id))];
        alerts.push({
          id: 'pending_escalation',
          type: 'pending_escalation' as AlertType,
          severity: 'warning' as AlertSeverity,
          title: 'Escalation Pendente',
          description: `${pendingEscalations.length} escalation(s) sem resposta há 8+ horas`,
          count: pendingEscalations.length,
          session_ids: uniqueSessionIds,
        });
      }

      const { data: unansweredSessions } = await supabase
        .from('v_sessions_with_sla')
        .select('id')
        .neq('status', 'resolved')
        .gte('unread_count', 3);

      if (unansweredSessions && unansweredSessions.length > 0) {
        alerts.push({
          id: 'multiple_unanswered',
          type: 'multiple_unanswered' as AlertType,
          severity: 'warning' as AlertSeverity,
          title: 'Múltiplas Não Lidas',
          description: `${unansweredSessions.length} sessão(ões) com 3+ mensagens sem resposta`,
          count: unansweredSessions.length,
          session_ids: unansweredSessions.map(s => s.id).filter(Boolean) as string[],
        });
      }

      return alerts.sort((a, b) => {
        if (a.severity === 'critical' && b.severity !== 'critical') return -1;
        if (a.severity !== 'critical' && b.severity === 'critical') return 1;
        return b.count - a.count;
      });
    },
    refetchInterval: 30000,
  });

  return {
    metrics: metricsQuery.data,
    metricsLoading: metricsQuery.isLoading,
    metricsError: metricsQuery.error,
    alerts: alertsQuery.data || [],
    alertsLoading: alertsQuery.isLoading,
    alertsError: alertsQuery.error,
    refetch: () => {
      metricsQuery.refetch();
      alertsQuery.refetch();
    },
  };
}

// Hook for SLA config management
export function useSlaConfig() {
  const configQuery = useQuery({
    queryKey: ['sla-config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sla_config')
        .select('*')
        .order('demand_type')
        .order('priority');

      if (error) throw error;
      return data || [];
    },
  });

  const updateConfig = async (id: string, updates: {
    first_response_minutes?: number;
    resolution_minutes?: number;
    internal_escalation_minutes?: number;
    is_active?: boolean;
  }) => {
    const { error } = await supabase
      .from('sla_config')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
    configQuery.refetch();
  };

  return {
    configs: configQuery.data || [],
    isLoading: configQuery.isLoading,
    error: configQuery.error,
    updateConfig,
    refetch: configQuery.refetch,
  };
}
